import { useEffect } from "react";
import { Toaster } from "react-hot-toast";
import Router from "./routes/Router";
import { AuthContextProvider } from "./store/auth-context";
import { BrandingContextProvider } from "./store/branding-context";
import AppConstants from "./configs/AppConstants"
import { getRuntimeEnv } from "./configs/runtimeConfig";
import { GoogleOAuthProvider } from "@react-oauth/google";

function App() {
  useEffect(() => {
    const normalizeHexColor = (input) => {
      if (!input) return null;
      const value = String(input).trim();
      const normalized = value.startsWith("#") ? value : `#${value}`;
      const hex = normalized.slice(1);
      if (/^[0-9a-fA-F]{3}$/.test(hex)) {
        return (
          "#" +
          hex
            .split("")
            .map((c) => c + c)
            .join("")
            .toLowerCase()
        );
      }
      if (/^[0-9a-fA-F]{6}$/.test(hex)) return `#${hex.toLowerCase()}`;
      return null;
    };

    const clamp255 = (n) => Math.max(0, Math.min(255, Math.round(n)));

    const hexToRgb = (hexColor) => {
      const hex = normalizeHexColor(hexColor);
      if (!hex) return null;
      const raw = hex.slice(1);
      const r = parseInt(raw.slice(0, 2), 16);
      const g = parseInt(raw.slice(2, 4), 16);
      const b = parseInt(raw.slice(4, 6), 16);
      return { r, g, b };
    };

    const rgbToHex = ({ r, g, b }) => {
      const to2 = (n) => clamp255(n).toString(16).padStart(2, "0");
      return `#${to2(r)}${to2(g)}${to2(b)}`;
    };

    // factor in [-1..1], negative => darker, positive => lighter
    const adjustColor = (hexColor, factor) => {
      const rgb = hexToRgb(hexColor);
      if (!rgb) return null;
      const f = Math.max(-1, Math.min(1, factor));
      const target = f >= 0 ? 255 : 0;
      const amt = Math.abs(f);
      return rgbToHex({
        r: rgb.r + (target - rgb.r) * amt,
        g: rgb.g + (target - rgb.g) * amt,
        b: rgb.b + (target - rgb.b) * amt,
      });
    };

    const applyBrandingColor = (hexColor) => {
      const root = document.documentElement;
      const c = normalizeHexColor(hexColor);
      if (!c) return;

      // Core "primary" tokens used across the UI
      root.style.setProperty("--cf-primary-color", c);
      root.style.setProperty("--button-color-primary", c);
      root.style.setProperty("--cf-button-color", c);
      root.style.setProperty("--dont-have-account-sign-up-text", c);
      root.style.setProperty(
        "--issued-screen-tab-button-selected-text-color",
        c,
      );

      // Derived variants used for hover/accents
      const dark = adjustColor(c, -0.12);
      const darkBlue = adjustColor(c, -0.2);
      const light = adjustColor(c, 0.12);
      const cardHover = adjustColor(c, 0.28);
      if (dark) root.style.setProperty("--cf-primary-dark-color", dark);
      if (darkBlue)
        root.style.setProperty("--cf-primary-dark-blue-color", darkBlue);
      if (light) root.style.setProperty("--cf-primary-light-color", light);
      if (cardHover)
        root.style.setProperty("--cf-primary-card-hover-color", cardHover);

      // Button hover uses an alpha version of the primary
      const rgb = hexToRgb(c);
      if (rgb) {
        root.style.setProperty(
          "--button-color-primary-hover",
          `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.86)`,
        );
      }
    };

    // This function will be called from BrandingProvider
    const setupBranding = (organisationData) => {
      if (organisationData) {
        if (organisationData.organisation_color) {
          applyBrandingColor(organisationData.organisation_color);
        }

        if (organisationData.organisation_favicon) {
          const link =
            document.querySelector("link[rel~='icon']") ||
            document.createElement("link");

          link.rel = "icon";
          link.href = organisationData.organisation_favicon;
          document.head.appendChild(link);
        }
      }
    };

    // Store the setupBranding function globally so BrandingProvider can call it
    window.setupBranding = setupBranding;

    // Cleanup function
    return () => {
      delete window.setupBranding;
    };
  }, []);

  return (
    <div className="App">
      <GoogleOAuthProvider
        clientId={
          getRuntimeEnv("REACT_APP_GOOGLE_CLIENT_ID") || AppConstants.CLIENT_ID
        }
      >
        <BrandingContextProvider>
          <AuthContextProvider>
            <Toaster
              position="bottom-right"
              reverseOrder={true}
              containerStyle={{ zIndex: 60 }}
            />
            <Router />
          </AuthContextProvider>
        </BrandingContextProvider>
      </GoogleOAuthProvider>
    </div>
  );
}

export default App;
